import type { AuditQuery } from '@palaccount/contracts';
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';

import { api } from '@/api';
import { throwApiError } from '@/lib/server-messages';

/** DSH-09's data layer. Read-only, apart from the export that logs itself. */

const KEY = ['audit'] as const;

/**
 * `keepPreviousData` for the reason the reports screens use it and one more:
 * this is the only PAGED screen in the product, and a page change that
 * dropped the table back to a skeleton would lose the reader's place on every
 * Next — which on a log somebody is reading row by row is the difference
 * between an investigation and a slideshow.
 */
export function useAuditLog(query: AuditQuery) {
  return useQuery({
    queryKey: [...KEY, query],
    queryFn: async () => {
      const res = await api.audit.list({ query });
      if (res.status !== 200) throwApiError(res.status, res.body);
      return res.body;
    },
    placeholderData: keepPreviousData,
  });
}

/**
 * The export. A MUTATION rather than a query, because it is one — it writes an
 * Export entry into the log (BR-DSH-09-21), and a cache that could replay it
 * would write two.
 */
export function useAuditExport() {
  return useMutation({
    mutationFn: async (body: Omit<AuditQuery, 'page' | 'pageSize'>) => {
      const res = await api.audit.exportCsv({ body });
      if (res.status !== 200) throwApiError(res.status, res.body);
      return res.body;
    },
  });
}
